import type { WithId } from '@octopus-do/octopus-project-core'
import { createAction } from '@reduxjs/toolkit'
import type {
  AddProjectPayload,
  FolderAccessRequestPayload,
  FolderMovePayload,
  FolderPayload,
  FolderTitledPayload,
  FolderUpdatePayload,
  FolderVisibilityPayload,
  MassArchivePayload,
  MemberAccessUpdate,
  MemberInvitePayload,
  MemberParams,
  MemberRolePayload,
  MembersPushPayload,
  MembersSetPayload,
  MembersUpdatePayload,
  NewFolderPayload,
  ProjectCreatedPayload,
  ProjectMovePayload,
  ProjectUpdatePayload,
  RenameTeamPayload,
  SetFoldersPayload,
  TeamMembersPayload,
  UpdateAvatarPayload,
  WithWorkspace,
  Workspace,
  WorkspaceUpdatePayload,
} from '@store/reducers/dashboard/types'

export const FolderAccessRequestAction =
  createAction<FolderAccessRequestPayload>('INTERNAL_DASHBOARD_FOLDERS_ACCESS')

const FolderActions = {
  delete: createAction<FolderPayload>('DASHBOARD_FOLDERS_DELETE'),
  move: createAction<FolderMovePayload>('DASHBOARD_FOLDERS_MOVE'),
  rename: createAction<FolderTitledPayload>('DASHBOARD_FOLDERS_RENAME'),
  create: createAction<NewFolderPayload>('DASHBOARD_FOLDERS_CREATE'),
  updateVisibility: createAction<FolderVisibilityPayload>(
    'DASHBOARD_FOLDERS_VISIBILITY_UPDATE'
  ),
  updateMemberAccess: createAction<MemberAccessUpdate>(
    'DASHBOARD_FOLDERS_MEMBER_ACCESS'
  ),
}

const MembersActions = {
  invite: createAction<MemberInvitePayload>('DASHBOARD_MEMBERS_INVITE'),
  cancelInvitation: createAction<MemberParams>(
    'DASHBOARD_MEMBERS_INVITATION_CANCEL'
  ),
  changeRole: createAction<MemberRolePayload>('DASHBOARD_MEMBERS_CHANGE_ROLE'),
  delete: createAction<MemberParams>('DASHBOARD_MEMBERS_DELETE'),
  leaveTeam: createAction<WithWorkspace>('DASHBOARD_MEMBERS_LEAVE_TEAM'),
  suspend: createAction<MemberParams>('DASHBOARD_MEMBERS_SUSPEND'),
  unsuspend: createAction<MemberParams>('DASHBOARD_MEMBERS_UNSUSPEND'),
}

const ProjectActions = {
  delete: createAction<WithId>('DASHBOARD_PROJECTS_DELETE'),
  leave: createAction<WithId>('DASHBOARD_PROJECTS_LEAVE'),
  archive: createAction<WithId>('DASHBOARD_PROJECTS_ARCHIVE'),
  unarchive: createAction<WithId>('DASHBOARD_PROJECTS_UNARCHIVE'),
  pin: createAction<WithId>('DASHBOARD_PROJECTS_PIN'),
  unpin: createAction<WithId>('DASHBOARD_PROJECTS_UNPIN'),
  moveToWorkspace: createAction<ProjectMovePayload>(
    'DASHBOARD_PROJECTS_TO_WORKSPACE'
  ),
  duplicate: createAction<WithId>('DASHBOARD_PROJECTS_DUPLICATE'),
  share: createAction<WithId>('DASHBOARD_PROJECTS_SHARE'),
  create: createAction<WithWorkspace>('DASHBOARD_PROJECTS_CREATE'),
  created: createAction<ProjectCreatedPayload>('DASHBOARD_PROJECTS_CREATED'),
}

export const LegacyMemberSet = createAction<TeamMembersPayload>(
  'INTERNAL_DASHBOARD_MEMBERS_ADD'
)

export const TeamSettingsActions = {
  rename: createAction<RenameTeamPayload>('DASHBOARD_TEAM_RENAME'),
  updateAvatar: createAction<UpdateAvatarPayload>(
    'DASHBOARD_TEAM_UPDATE_AVATAR'
  ),
  removeAvatar: createAction<WithWorkspace>('DASHBOARD_TEAM_REMOVE_AVATAR'),
}

export const DashboardActions = {
  init: createAction('DASHBOARD_INIT'),
  folders: FolderActions,
  projects: ProjectActions,
  members: MembersActions,
  teams: TeamSettingsActions,
}

export const InternalProjectActions = {
  move: createAction<ProjectMovePayload>('INTERNAL_DASHBOARD_PROJECTS_MOVE'),
  delete: createAction<WithId>('INTERNAL_DASHBOARD_PROJECTS_DELETE'),
  add: createAction<AddProjectPayload>('INTERNAL_DASHBOARD_PROJECTS_ADD'),
  update: createAction<ProjectUpdatePayload>(
    'INTERNAL_DASHBOARD_PROJECTS_UPDATE'
  ),
  massArchive: createAction<MassArchivePayload>(
    'INTERNAL_DASHBOARD_PROJECTS_MASS_ARCHIVE'
  ),
}

export const InternalFolderActions = {
  set: createAction<SetFoldersPayload>('INTERNAL_DASHBOARD_FOLDERS_SET'),
  order: createAction('INTERNAL_DASHBOARD_FOLDERS_SWAP'),
  delete: createAction<FolderPayload>('INTERNAL_DASHBOARD_FOLDERS_DELETE'),
  update: createAction<FolderUpdatePayload>(
    'INTERNAL_DASHBOARD_FOLDERS_UPDATE'
  ),
  add: createAction<NewFolderPayload>('INTERNAL_DASHBOARD_FOLDERS_ADD'),
}

export const InternalMembersActions = {
  update: createAction<MembersUpdatePayload>(
    'INTERNAL_DASHBOARD_MEMBER_UPDATE'
  ),
  remove: createAction<MemberParams>('INTERNAL_DASHBOARD_MEMBER_REMOVE'),
  set: createAction<MembersSetPayload>('INTERNAL_DASHBOARD_MEMBERS_SET'),
  push: createAction<MembersPushPayload>('INTERNAL_DASHBOARD_MEMBERS_PUSH'),
  fetchList: createAction<WithWorkspace>('INTERNAL_DASHBOARD_MEMBERS_FETCH'),
}

export const InternalDashboardActions = {
  setWorkspaces: createAction<Workspace[]>('INTERNAL_DASHBOARD_SET_WORKSPACES'),
  delete: createAction<WithWorkspace>('INTERNAL_DASHBOARD_DELETE_WORKSPACE'),
  update: createAction<WorkspaceUpdatePayload>('INTERNAL_DASHBOARD_UPDATE'),
  renameRequest: createAction<RenameTeamPayload>(
    'INTERNAL_DASHBOARD_RENAME_REQUEST'
  ),
}
